import debounce from 'lodash.debounce'
import { FC, useEffect, useRef, useState } from 'react'
import { styled } from '../../lib/stitches'

/**
 * DPI_SCALE constant. You can adjust this value to improve performance or improve quality of the rendering
 */
const RESOLUTION = 1
const DPI_SCALE = devicePixelRatio || 2
const SEGMENT_WIDTH = 2
const SEGMENT_SPACING = SEGMENT_WIDTH
const DEFAULT_COLOR = '#ffffff'

const Container = styled('div', {
  display: 'flex',
  flex: 1,
  overflow: 'hidden'
})

export interface AudioVisualizationOptions {
  color: string
  segmentWidth: number
  segmentSpacing: number
  resolution: number
}

function minmax(arr: Float32Array): [min: number, max: number] {
  return [Math.min.apply(null, arr), Math.max.apply(null, arr)]
}

/**
 * Draws visualization of the normalized PCM audio data.
 * @param {HTMLCanvasElement} canvas expects an `HTMLCanvasElement` to draw on
 * @param {Float32Array} data `Float32Array` representing PCM data, where min value is -1 and max is 1
 * @example
 * const data = audioBuffer.getChannelData(0) // getting audio data from the buffer
 * drawSample(canvas, data)
 */
export const drawSample = (
  canvas: HTMLCanvasElement,
  data: Float32Array,
  {
    segmentWidth = SEGMENT_WIDTH,
    color = DEFAULT_COLOR,
    segmentSpacing = SEGMENT_SPACING,
    resolution = RESOLUTION
  }: Partial<AudioVisualizationOptions> = {},
  sampleCache: { sampleData: Float32Array; multiplier: number } = null
) => {
  if (!canvas) return null
  /** resolution multiplier. covers retina screens + render quality tweaks */
  const { width, height } = canvas
  const resMultiplier = resolution * DPI_SCALE
  const ss = resMultiplier * segmentSpacing
  const sw = resMultiplier * segmentWidth
  const ctx = canvas.getContext('2d')
  const segmentCount = Math.floor(width / (sw + ss))
  const yCenter = height / 2
  const sampleSize = Math.floor(data.length / segmentCount)
  // TODO: FFT + A-weighting https://github.com/audiojs/a-weighting
  const sampleData: Float32Array = sampleCache
    ? sampleCache.sampleData
    : data.filter((_, i) => i % sampleSize === 0).map((e) => Math.abs(e))
  // another way of showing nice graphics
  // for (let i = 0; i < segmentCount; i++) {
  //   sampleData[i] =
  //     data.slice(i * segmentCount, (i + 1) * segmentCount).reduce((p, e) => Math.abs(e) + p, 0) /
  //     segmentCount
  // }

  // this multiplier ensures that the max sample value is always 1, for the dramatic effect
  const multiplier: number = sampleCache
    ? sampleCache.multiplier
    : Math.pow(Math.max.apply(null, sampleData), -1)
  ctx.lineCap = 'round'
  ctx.lineWidth = sw
  ctx.strokeStyle = color
  ctx.beginPath()
  for (let i = 0; i < segmentCount; i++) {
    const x = (sw + ss) * i + sw / 2
    const y = sampleData[i] * multiplier * (yCenter - sw / 2)
    ctx.moveTo(x, yCenter - y)
    ctx.lineTo(x, yCenter + y)
  }
  ctx.stroke()
  return { sampleData, multiplier }
}

export const adjustCanvasResolution = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  resolution: number
) => {
  canvas.width = Math.floor(DPI_SCALE * resolution * width)
  canvas.height = Math.floor(DPI_SCALE * resolution * height)
}

interface AudioVisualizerProps {
  audioBuffer?: AudioBuffer
  audioArray?: Float32Array
  options?: Partial<AudioVisualizationOptions>
}

const AudioVisualizer: FC<AudioVisualizerProps> = ({ audioBuffer, audioArray, options = {} }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [sampleCache, setSampleCache] = useState<{ sampleData: Float32Array; multiplier: number }>(
    null
  )

  useEffect(() => {
    const canvas = canvasRef.current
    const resize = debounce((canvas: HTMLCanvasElement, width: number, height: number) => {
      if (!canvas) return
      adjustCanvasResolution(canvas, width, height, options?.resolution || RESOLUTION)
      let floatData = audioArray || audioBuffer.getChannelData(0)
      const cachedData = drawSample(canvasRef.current, floatData, options, sampleCache)
      // setSampleCache(cachedData) // TODO: add this back to cache the waveform data, improve performance
    }, 40)
    const observer = new ResizeObserver((entries) => {
      if (!entries[0]) return
      const { width, height } = entries[0].contentRect
      resize(canvas, width, height)
    })
    observer.observe(canvas)
    return () => {
      observer.disconnect()
      setSampleCache(null)
    }
  }, [audioBuffer, options.color, options.resolution, audioArray])

  return (
    <Container>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', minHeight: '0' }} />
    </Container>
  )
}

export default AudioVisualizer
